/** @jsxImportSource @emotion/react */
import * as ui from "@material-ui/core";
import { definitions } from "../../../schema/api";
import { DataStore, sortType } from "../../../context/DataStoreContext";
import Folder from "@material-ui/icons/Folder";
import CloudDownload from "@material-ui/icons/CloudDownload";
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ArrowDropUp from "@material-ui/icons/ArrowDropUp";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import { mediaQurey } from "../../../style";

interface ListProps {
  list: definitions["ShareResource"][];
  targetId?: number;
  checkedFiles: definitions["ShareResource"][];
  sorts?: DataStore["shareResource"]["listParameters"][number]["sorts"];
  enableUpdate: boolean;
  onChangeSort: (key: keyof definitions["ShareResource"]) => void | Promise<void>;
  onClickFolder: (id: number) => void | Promise<void>;
  onCheckFile: (file: definitions["ShareResource"]) => void | Promise<void>;
  onCheckBulk: () => void | Promise<void>;
  onSelectFile: (file: definitions["ShareResource"]) => void | Promise<void>;
  onDownloadFile: (file: definitions["ShareResource"]) => void | Promise<void>;
  onDeleteBulk: () => void | Promise<void>;
}

const formatDate = (date: string) => new Date(date).toLocaleString("ja-JP", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const labels: { [key: string]: string } = {
  delete: "削除",
  download: "ダウンロード",
  notFound: "現在共有資料はありません",
};

const styles = {
  list: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    marginBlock: "unset",
    paddingInline: "unset",
    ["& > li"]: {
      display: "contents",
      cursor: "pointer",
      ["&:nth-of-type(n+2):hover > div"]: {
        backgroundColor: "#ddbbee",
      },
      ["& > div"]: {
        display: "flex",
        alignItems: "center",
        gap: "0.2rem",
        borderBottom: "solid 0.1rem #ccc",
        height: "3.75rem",
        padding: "0 1rem",
        [mediaQurey("max", "sp")]: {
          height: "5rem",
        },
      },
      ["& input"]: {
        transform: "scale(1.2)",
        [mediaQurey("max", "sp")]: {
          transform: "scale(1.5)",
          marginRight: "1rem",
        },
      },
      ["& > img"]: {
        display: "inline-block",
        marginLeft: "auto",
        maxWith: "15rem",
        maxHeight: "10rem",
      },
      ["& > div:nth-of-type(3) svg"]: {
        transform: "scale(1.2)",
      },
    },
  },
  target: {
    backgroundColor: "#aa88bb",
  },
  operationWrapper: {
    position: "relative" as const,
    display: "flex",
    alignItems: "center",
  },
  fileName: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    ["&:hover"]: {
      color: "#000088",
      textDecoration: "underline",
    },
  },
  operation: {
    position: "absolute" as const,
    top: "0",
    left: "0",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexWrap: "nowrap" as const,
    whiteSpace: "nowrap",
    height: "100%",
    backgroundColor: "#ffffff",
  },
  notFound: {
    gridColumn: "1 / 4",
    borderBottom: "none !important",
  },
  sortIcon: (operated: boolean) => ({
    display: "flex",
    alignItems: "inherit",
    justifyContent: "inherit",
    ["& > svg"]: {
      ...(operated ? {} : { fill: "rgba(0, 0, 0, 0.54)" }),
      width: "1em",
      height: "1em",
      display: "inline-block",
      fontSize: "1.5rem",
      transition: "fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      flexShrink: "0",
      userSelect: "none" as const,
    },
  }),
};

function List(props: ListProps) {
  const sorts = props.sorts || [];
  const nameSort = sorts.find(({ key }) => key === "name");
  const dateSort = sorts.find(({ key }) => key === "date");

  const onClick = (item: definitions["ShareResource"], event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    item.type === "folder"
      ? props.onClickFolder(item.id)
      : props.onDownloadFile(item);
  };

  const onCheck = (item: definitions["ShareResource"], event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    event.stopPropagation();
    props.onCheckFile(item);
  };

  const onSelect = (item: definitions["ShareResource"], event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    if (props.enableUpdate || item.type !== "folder") {
      props.onSelectFile(item);
    } else if (item.type === "folder") {
      props.onClickFolder(item.id);
    }
  };

  return (
    <ul css={styles.list}>
      <li key="shareresource.index">
        <div>
          {props.enableUpdate && (
            <label>
              <input
                type="checkbox"
                onChange={props.onCheckBulk}
                checked={!!props.list.length && props.checkedFiles.length === props.list.length}
              />
            </label>
          )}
          <div
            onClick={() => props.onChangeSort("name")}
            css={styles.operationWrapper}
          >
            資料名
            <span css={styles.sortIcon(!!nameSort?.order)}>
              {nameSort?.order === sortType.desc ? (
                <ArrowDropDown />
              ) : (
                <ArrowDropUp />
              )}
            </span>
            {props.enableUpdate && !!props.checkedFiles.length && (
              <div css={styles.operation}>
                <div>
                  チェックしたフォルダ・資料を
                </div>
                <ui.Grid item>
                  <ui.Button
                    variant="contained"
                    color="secondary"
                    type="submit"
                    onClick={props.onDeleteBulk}
                  >
                    {labels.delete}
                  </ui.Button>
                </ui.Grid>
              </div>
            )}
          </div>
        </div>
        <div>
          作成日
          <span
            onClick={() => props.onChangeSort("date")}
            css={styles.sortIcon(!!dateSort?.order)}
          >
              {dateSort?.order === sortType.desc ? (
                <ArrowDropDown />
              ) : (
                <ArrowDropUp />
              )}
          </span>
        </div>
        <div></div>
      </li>
      {props.list.map((item, i) => (
        <li
          onClick={(e) => onSelect(item, e)}
          {...(item.id === props.targetId ? { css: styles.target } : {})} key={`shareresource.${i}`}
        >
          <div>
            {props.enableUpdate && (
              <label>
                <input
                  type="checkbox"
                  onClick={(e) => onCheck(item, e)}
                  checked={props.checkedFiles.includes(item)}
                />
              </label>
            )}
            <span css={styles.fileName}>
              {item.type === "folder" && (
                <Folder />
              )}
              {item.name}
            </span>
          </div>
          <div>{formatDate(item.date)}</div>
          {item.type !== "folder" ? (
            <div onClick={(e) => onClick(item, e)}>
              <CloudDownload />
            </div>
          ) : (
            <div onClick={(e) => onClick(item, e)}>
              <ArrowForwardIosIcon />
            </div>
          )}
        </li>
      ))}
      {!props.list.length && (
        <li key="shareresource.not-found">
          <div css={styles.notFound}>{labels.notFound}</div>
        </li>
      )}
    </ul>
  );
}

export { List };
